import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck, faBuilding, faTriangleExclamation, faHourglassHalf,
  faCircleCheck, faLocationDot, faArrowTrendUp,
} from '@fortawesome/free-solid-svg-icons';
import './acts.css';
import './Act3_Portfolio.css';

const PROPERTIES = [
  { name: 'UCL East',             city: 'London',      assets: 198, pass: 86, fail: 0,  due: 12, untested: 100, code: 'UCL44' },
  { name: 'Sterling Tower',       city: 'Manchester',  assets: 64,  pass: 58, fail: 2,  due: 3,  untested: 1,   code: 'STRL16' },
  { name: 'Riverside Plaza',      city: 'Leeds',       assets: 52,  pass: 49, fail: 0,  due: 3,  untested: 0,   code: 'RVRS17' },
  { name: 'One Canada Square',    city: 'London',      assets: 112, pass: 102, fail: 1, due: 7,  untested: 2,   code: 'OCS30' },
  { name: 'Soho Lighthouse',      city: 'London',      assets: 38,  pass: 28, fail: 3,  due: 6,  untested: 1,   code: 'SHLG31' },
  { name: 'Primrose House',       city: 'Bristol',     assets: 24,  pass: 24, fail: 0,  due: 0,  untested: 0,   code: 'PRMR21' },
];

function compliancePct(p) {
  return p.assets > 0 ? Math.round((p.pass / p.assets) * 100) : 0;
}
function risk(p) {
  if (p.fail > 0) return 'high';
  if (p.untested >= 10 || p.due > 5) return 'medium';
  if (p.due > 0 || p.untested > 0) return 'low';
  return 'ok';
}
const RISK_META = {
  high:   { label: 'Action required', color: '#dc2626' },
  medium: { label: 'Investigate',      color: '#d97706' },
  low:    { label: 'Monitor',          color: '#0ea5e9' },
  ok:     { label: 'All clear',        color: '#0d9488' },
};

export default function Act3Portfolio({ active }) {
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (!active) {
      setVisibleCount(0);
      return;
    }
    let i = 0;
    const timers = [];
    timers.push(setTimeout(() => setVisibleCount(1), 200));
    const step = () => {
      i++;
      if (i <= PROPERTIES.length) {
        setVisibleCount(i);
        timers.push(setTimeout(step, 120));
      }
    };
    timers.push(setTimeout(step, 320));
    return () => timers.forEach(clearTimeout);
  }, [active]);

  // Totals across portfolio
  const totalAssets = PROPERTIES.reduce((s, p) => s + p.assets, 0);
  const totalPass   = PROPERTIES.reduce((s, p) => s + p.pass, 0);
  const totalFail   = PROPERTIES.reduce((s, p) => s + p.fail, 0);
  const totalDue    = PROPERTIES.reduce((s, p) => s + p.due, 0);
  const totalUntested = PROPERTIES.reduce((s, p) => s + p.untested, 0);
  const portfolioPct = Math.round((totalPass / totalAssets) * 100);

  return (
    <div className="act act-3">
      {/* ── Narrative ── */}
      <div className="act-copy">
        <span className="act-eyebrow">
          <span className="act-eyebrow-dot" /> Act 3 · Portfolio
        </span>
        <h1 className="act-title">
          The whole portfolio. <span className="act-title-accent">One glance.</span>
        </h1>
        <p className="act-lede">
          Fifty buildings, one dashboard. Compliance colour-coded per property,
          critical issues bubbled to the top, every drill-down one click away.
          Find the red one, fix it, move on.
        </p>
        <ul className="act-proofs">
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>Red / amber / green at a glance.</strong> No more spreadsheets to triage your week.</span>
          </li>
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>Untested assets surface automatically.</strong> Not tested = not compliant.</span>
          </li>
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>One-click portfolio PDF</strong> — hand a single document to any board or insurer.</span>
          </li>
        </ul>
      </div>

      {/* ── Stage ── */}
      <div className="act-stage">
        <div className="act-device a3-device">
          <div className="act-device-chrome">
            <div className="act-device-dots"><span /><span /><span /></div>
            <div className="act-device-addr">cloudsafe.uk/fm/portfolio</div>
          </div>

          <div className="a3-body">
            {/* Summary strip */}
            <div className="a3-summary">
              <div className="a3-summary-main">
                <div className="a3-summary-label">Portfolio compliance</div>
                <div className="a3-summary-pct">
                  <span className="a3-summary-pct-val">{portfolioPct}</span>
                  <span className="a3-summary-pct-unit">%</span>
                </div>
                <div className="a3-summary-bar">
                  <div
                    className="a3-summary-bar-fill"
                    style={{ width: `${portfolioPct}%` }}
                  />
                </div>
              </div>
              <div className="a3-summary-divider" />
              <div className="a3-summary-stats">
                <div className="a3-summary-stat">
                  <FontAwesomeIcon icon={faBuilding} className="a3-summary-ico a3-c-navy" />
                  <div>
                    <div className="a3-summary-stat-val">{PROPERTIES.length}</div>
                    <div className="a3-summary-stat-lbl">Properties</div>
                  </div>
                </div>
                <div className="a3-summary-stat">
                  <FontAwesomeIcon icon={faCircleCheck} className="a3-summary-ico a3-c-teal" />
                  <div>
                    <div className="a3-summary-stat-val">{totalPass}</div>
                    <div className="a3-summary-stat-lbl">Compliant</div>
                  </div>
                </div>
                <div className="a3-summary-stat">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="a3-summary-ico a3-c-red" />
                  <div>
                    <div className="a3-summary-stat-val">{totalFail}</div>
                    <div className="a3-summary-stat-lbl">Failed</div>
                  </div>
                </div>
                <div className="a3-summary-stat">
                  <FontAwesomeIcon icon={faHourglassHalf} className="a3-summary-ico a3-c-amber" />
                  <div>
                    <div className="a3-summary-stat-val">{totalDue + totalUntested}</div>
                    <div className="a3-summary-stat-lbl">Need attention</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Property grid */}
            <div className="a3-grid">
              {PROPERTIES.map((p, i) => {
                const pct = compliancePct(p);
                const r = risk(p);
                const meta = RISK_META[r];
                return (
                  <article
                    key={p.name}
                    className={`a3-card a3-risk-${r} ${i < visibleCount ? 'is-in' : ''}`}
                    style={{ '--risk-color': meta.color, animationDelay: `${i * 80}ms` }}
                  >
                    <header className="a3-card-head">
                      <div className="a3-card-ico">
                        <FontAwesomeIcon icon={faBuilding} />
                      </div>
                      <div className="a3-card-head-text">
                        <div className="a3-card-name">{p.name}</div>
                        <div className="a3-card-loc">
                          <FontAwesomeIcon icon={faLocationDot} /> {p.city}
                          <span className="a3-card-code">· {p.code}</span>
                        </div>
                      </div>
                      <span className="a3-card-risk">{meta.label}</span>
                    </header>

                    <div className="a3-card-meter">
                      <div className="a3-card-meter-head">
                        <span>Compliance</span>
                        <span className="a3-card-meter-val">{pct}%</span>
                      </div>
                      <div className="a3-card-meter-track">
                        <div
                          className="a3-card-meter-fill"
                          style={{ width: `${pct}%`, background: meta.color }}
                        />
                      </div>
                    </div>

                    <div className="a3-card-counts">
                      <span className="a3-card-count a3-c-navy">
                        <strong>{p.assets}</strong> assets
                      </span>
                      {p.fail > 0 && (
                        <span className="a3-card-count a3-c-red">
                          <strong>{p.fail}</strong> failed
                        </span>
                      )}
                      {p.due > 0 && (
                        <span className="a3-card-count a3-c-amber">
                          <strong>{p.due}</strong> due soon
                        </span>
                      )}
                      {p.untested > 0 && (
                        <span className="a3-card-count a3-c-amber">
                          <strong>{p.untested}</strong> untested
                        </span>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>

            {/* Trend footnote */}
            <div className="a3-trend">
              <FontAwesomeIcon icon={faArrowTrendUp} />
              <span>
                Portfolio compliance up <strong>+6.2 points</strong> since last month.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
